import React, { useState, useEffect, useRef } from "react";
import "./styles/styles.css";
import "../../styles/global.css";
import TableRow from "./components/TableRow";
import TableHeader from "../TableHeader/TableHeader";

const TableFirst = ({
  options,
  data,
  onClick,
  onDownloadClick,
  isLoadingData,
  scrollPosition = 0,
  query,
  onDeleteClick,
  onSortChange,
  sortField,
  sortDirection,
  selectedStatusOptions,
  setSelectedStatusOptions,
}) => {
  const scrollRef = useRef(null);

  // Below 999px, rows always render as compact two-line cards instead of
  // the narrow, horizontally-scrolled columns — there's no benefit to First
  // tier's simpler tables (just events/persons) in offering an escape hatch
  // back to the cramped column view, so unlike other tiers there's no
  // seeAllColumns toggle here at all.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 999,
  );

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 999);
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const compact = isMobile;

  // Virtualization assumes every row is exactly ITEM_HEIGHT tall, so the
  // compact card height below (see .table-row-compact CSS) must match this.
  const ITEM_HEIGHT = compact ? 84 : 50;
  const BUFFER = 10;
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [visibleEndIndex, setVisibleEndIndex] = useState(100);

  const handleScroll = () => {
    if (!scrollRef.current) return;

    const scrollTop = scrollRef.current.scrollTop;
    const clientHeight = scrollRef.current.clientHeight;

    const startIndex = Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER;
    const endIndex =
      Math.ceil((scrollTop + clientHeight) / ITEM_HEIGHT) + BUFFER;

    setVisibleStartIndex(Math.max(0, startIndex));
    setVisibleEndIndex(Math.min(updatedData.length, endIndex));
  };

  const updatedData = data?.map((item) => {
    const widthValues = {};

    options.forEach((option) => {
      widthValues[option.name.toLowerCase()] = option.width;
    });

    return { ...item, width: widthValues };
  });
  const totalWidth = options.reduce((sum, opt) => sum + opt.width, 0);

  const placeholderRows = Array.from({ length: 6 }, (_, index) => ({
    id: `placeholder-${index}`,
    className: "animated-bg-text animated-bg",
    rowData: Array.from({ length: options.length }, (_, i) => ({
      key: String(i),
      width: options[i].width,
      className: `${options[i].name} ${options[i].class}`,
    })),
  }));

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollPosition;
    }
  }, [scrollPosition]);

  // Recompute the visible window whenever the row height changes (resize
  // across 999px) instead of waiting for the next scroll event — and
  // whenever the data length changes, since the
  // very first run of this effect fires before an async fetch resolves
  // (updatedData still []), which would otherwise clamp visibleEndIndex to
  // 0 and leave it there forever once real data arrives (nothing to
  // re-trigger the clamp without this dependency).
  useEffect(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ITEM_HEIGHT, updatedData.length]);

  return (
    <div className="flex-direction-column gap-12 items-end table-first-wrapper">
      <div className="flex-direction-column width-100 table-first">
        <TableHeader
          options={options}
          totalWidth={totalWidth}
          seeAllColumns={false}
          hideExpandToggle
          hasMany={updatedData.length > 9}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
          selectedStatusOptions={selectedStatusOptions}
          setSelectedStatusOptions={setSelectedStatusOptions}
        />

        <div
          className="flex-directions-column table-first-content"
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {!isLoadingData ? (
            <>
              <div style={{ height: visibleStartIndex * ITEM_HEIGHT }} />
              {updatedData
                .slice(visibleStartIndex, visibleEndIndex)
                .map((rowData, index, arr) => (
                  <TableRow
                    key={rowData.id || index}
                    rowData={rowData}
                    totalWidth={totalWidth}
                    onClick={(key) => onClick(key)}
                    onDownloadClick={(key) => onDownloadClick(key)}
                    seeAllColumns={false}
                    isLoadingData={isLoadingData}
                    isLast={index === arr.length - 1}
                    query={query}
                    onDeleteClick={onDeleteClick}
                    compact={compact}
                  />
                ))}
              <div
                style={{
                  height: (updatedData.length - visibleEndIndex) * ITEM_HEIGHT,
                }}
              />
            </>
          ) : (
            <React.Fragment>
              {placeholderRows?.map((rowData, index) => (
                <TableRow
                  key={`placeholder-${index}`}
                  rowData={rowData.rowData}
                  totalWidth={totalWidth}
                  isPlaceholder={true}
                  seeAllColumns={false}
                  isLoadingData={isLoadingData}
                  query={query}
                  compact={compact}
                  isLast={index === placeholderRows.length - 1}
                />
              ))}
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableFirst;
